import * as THREE from "three";
import { PlanogramRenderer } from "./PlanogramRenderer.js";
import { PlanogramContainer } from "./PlanogramContainer.js";
import { PlanogramCameraFrame } from "./PlanogramCameraFrame.js";
import { OrbitControls } from "../OrbitControls.js";
import { PlangoramCameraAttributes, PlanogramContainerAttributes, PlanogramObjectAttributes, PlanogramRendererResources } from './Resources.js';

export class PlanogramCamera extends HTMLElement {

    static #lockVerticalObservedAttributes = [
        PlanogramObjectAttributes.Attributes.X,
        PlanogramObjectAttributes.Attributes.Y,
        PlanogramObjectAttributes.Attributes.Z,
        PlanogramObjectAttributes.Attributes.RotationX,
        PlanogramObjectAttributes.Attributes.RotationY,
        PlanogramObjectAttributes.Attributes.RotationZ,
        PlanogramContainerAttributes.Attributes.Width,
        PlanogramContainerAttributes.Attributes.Height,
        PlanogramContainerAttributes.Attributes.Depth,
        "width",
        "height",
        "depth",
        "position-x",
        "position-y",
        "position-z",
        "rotation-x",
        "rotation-y",
        "rotation-z"
    ];

    static #miniMapObservedAttributes = [...new Set([
        ...PlanogramCamera.#lockVerticalObservedAttributes,
        PlanogramObjectAttributes.Attributes.Selected
    ])];

    //#region Attributes
    static Attributes = PlangoramCameraAttributes.Attributes;
    //#endregion

    //#region Private properties
    /** @type { THREE.PerspectiveCamera | THREE.OrthographicCamera } */
    #camera;
    /** @type { MapControls | DollyControls } */
    #movementControls;
    #firstRender = true;
    #uid = `camera-${Math.random().toString(36).substring(2, 9)}`;
    /** @type { THREE.LineSegments | null } */
    #grid = null;
    /** @type { number | null } - Unique layer number for this camera's grid */
    #gridLayer = null;

    #gridChangeListener = () => this.#setUpGrid();

    static #nextGridLayer = 10;
    static #MAX_GRID_LAYER = 31;
    static #recycledGridLayers = [];
    /**@type {HTMLDivElement} */
    #tabItem;
    /**@type {HTMLSpanElement} */
    #text;
    /**@type {HTMLSpanElement} */
    #icon;
    /**@type {HTMLButtonElement} */
    #closebutton;
    #defaultZoom;
    /**@type {HTMLDivElement|null} */
    #lockVerticalScrollbar = null;
    /**@type {HTMLDivElement|null} */
    #lockVerticalScrollbarSpacer = null;
    #lockVerticalMetrics = null;
    #lockVerticalDirty = true;
    #lockVerticalInitialized = false;
    #lockVerticalDockKey = null;
    #containerObserver = null;
    #movementChangeListener = () => this.#handleMovementControlsChange();
    #lockVerticalScrollListener = evt => this.#handleLockVerticalScrollbarScroll(evt);
    #stopScrollEvent = evt => evt.stopPropagation();
    #lockVerticalScrollbarSyncing = false;
    #lockVerticalExpectedScrollLeft = null;
    #writingLockVerticalState = false;
    /**@type {HTMLDivElement|null} */
    #miniMapRoot = null;
    /**@type {HTMLCanvasElement|null} */
    #miniMapCanvas = null;
    /**@type {THREE.WebGLRenderer|null} */
    #miniMapRenderer = null;
    /**@type {THREE.OrthographicCamera|null} */
    #miniMapCamera = null;
    /**@type {HTMLDivElement|null} */
    #miniMapViewport = null;
    /**@type {HTMLDivElement|null} */
    #miniMapResizeHandle = null;
    #miniMapMetrics = null;
    #miniMapDirty = true;
    #miniMapPointerId = null;
    #miniMapDragOffset = null;
    #miniMapDraggingViewport = false;
    #miniMapPointerMoved = false;
    #miniMapResizing = false;
    #miniMapResizeOrigin = null;
    #miniMapPreferredSize = null;
    #miniMapPointerDownListener = evt => this.#handleMiniMapPointerDown(evt);
    #miniMapPointerMoveListener = evt => this.#handleMiniMapPointerMove(evt);
    #miniMapPointerUpListener = evt => this.#handleMiniMapPointerUp(evt);
    #miniMapPointerCancelListener = evt => this.#handleMiniMapPointerCancel(evt);
    //#endregion

    //#region Attribute Properties
    get cameraName() { return this.getAttribute(PlanogramCamera.Attributes.CameraName) || "Camera"; }
    get containerId() { return this.getAttribute(PlanogramCamera.Attributes.ContainerId); }
    get cameraFrameIndex() { return Number(this.getAttribute(PlanogramCamera.Attributes.CameraFrameIndex)) || 0; }
    get type() { return PlanogramCamera.cameraType.get(this.getAttribute(PlanogramCamera.Attributes.Type)) || PlanogramCamera.cameraType.get("Orthographic"); }
    get control() { return PlanogramCamera.cameraControl.get(this.getAttribute(PlanogramCamera.Attributes.Control)) || PlanogramCamera.cameraControl.get("Orbit"); }
    get positionX() { return this.#getNumberAttribute(PlanogramCamera.Attributes.PositionX, 0); }
    get positionY() { return this.#getNumberAttribute(PlanogramCamera.Attributes.PositionY, 1); }
    get positionZ() { return this.#getNumberAttribute(PlanogramCamera.Attributes.PositionZ, 2); }
    get zoom() { return Number(this.getAttribute(PlanogramCamera.Attributes.Zoom)) || 1; }
    get lookAtX() { return this.#getNumberAttribute(PlanogramCamera.Attributes.LookAtX, 0, false); }
    get lookAtY() { return this.#getNumberAttribute(PlanogramCamera.Attributes.LookAtY, 1, false); }
    get lookAtZ() { return this.#getNumberAttribute(PlanogramCamera.Attributes.LookAtZ, 2, false); }
    get zoomDisabled() { return this.hasAttribute(PlanogramCamera.Attributes.ZoomDisabled); }
    get active() { return this.hasAttribute(PlanogramCamera.Attributes.Active); }
    get dockRow() { return Number(this.getAttribute(PlanogramCamera.Attributes.DockRow)) || 0; }
    get dockColumn() { return Number(this.getAttribute(PlanogramCamera.Attributes.DockColumn)) || 0; }
    get disableClose() { return this.hasAttribute(PlanogramCamera.Attributes.DisableClose); }
    get miniMap() { return this.hasAttribute(PlanogramCamera.Attributes.MiniMap); }
    get lockVertical() { return this.hasAttribute(PlanogramCamera.Attributes.LockVertical); }
    get lockTab() { return this.hasAttribute(PlanogramCamera.Attributes.LockTab); }
    get focused() { return this.hasAttribute(PlanogramCamera.Attributes.Focused); }
    get collapsed() { return this.hasAttribute(PlanogramCamera.Attributes.Collapsed); }
    get icon() { return this.getAttribute(PlanogramCamera.Attributes.Icon); }
    get showGrid() { return this.hasAttribute(PlanogramCamera.Attributes.ShowGrid); }
    get defaultPosition() {
        if (this.hasAttribute(PlanogramCamera.Attributes.DefaultPosition)) {
            var position = this.getAttribute(PlanogramCamera.Attributes.DefaultPosition).split(",").map(x => Number(x));
            position = position.concat(Array(3 - position.length).fill(0));
            return new THREE.Vector3(position[0], position[1], position[2]);
        }
        return null;
    }
    get defaultLookAt() {
        if (this.hasAttribute(PlanogramCamera.Attributes.DefaultLookAt)) {
            var lookat = this.getAttribute(PlanogramCamera.Attributes.DefaultLookAt).split(",").map(x => Number(x));
            lookat = lookat.concat(Array(3 - lookat.length).fill(0));
            return new THREE.Vector3(lookat[0], lookat[1], lookat[2]);
        }
        return null;
    }

    static get observedAttributes() {
        return [...Object.values(PlanogramCamera.Attributes)];
    }

    //#endregion

    //#region Properties
    tabControlOffset = 0;
    get camera() { return this.#camera; }
    get cameraFrame() {
        return this.parentElement instanceof PlanogramCameraFrame ? this.parentElement : null;
    }
    get movementControls() { return this.#movementControls; }
    get cameraLookAt() { return new THREE.Vector3(this.lookAtX, this.lookAtY, this.lookAtZ); }
    get cameraPosition() { return new THREE.Vector3(this.positionX, this.positionY, this.positionZ); }
    get dockVector() {
        if (this.active) {
            const frame = this.cameraFrame;
            if (frame)
                return frame.getViewBox();
        }
        return null;
    }
    get uid() { return this.#uid; }
    get tabItem() { return this.#tabItem; }
    get firstRender() { return this.#firstRender; }
    get container() {
        if (this.containerId) {
            const renderer = PlanogramRenderer.get(this);
            if (!renderer) return null;
            return renderer.querySelector(`planogram-container[container-id='${this.containerId}']`);
        }
        return null;
    }
    get groupFrameId() { return this.container?.groupFrameId; }

    index = 0;
    pointerdownCoordinates = null;

    //#endregion

    constructor() {
        super();
        this.zoomOffset = 10;
        this.orthoDistance = 500;
        this.perspectiveCameraMaxDistance = 10000;
    }

    //#region Attribute Helpers
    #getNumberAttribute(name, axis, isPosition = true) {
        if (this.hasAttribute(name)) {
            var number = Number(this.getAttribute(name));
            if (!isNaN(number)) return number;
        }
        else if (isPosition && this.defaultPosition)
            return this.defaultPosition.getComponent(axis);
        else if (!isPosition && this.defaultLookAt)
            return this.defaultLookAt.getComponent(axis);
        return 0;
    }
    //#endregion

    //#region Camera Methods

    setBehaviour(behaviour) {
        if (behaviour !== PlanogramRenderer.behaviour.none) {
            if (behaviour === PlanogramRenderer.behaviour.reset)
                this.resetCamera();
            else if (behaviour === PlanogramRenderer.behaviour.fit)
                this.updateZoom(null, true, true);
            else if (behaviour === PlanogramRenderer.behaviour.both) {
                this.resetCamera();
                this.updateZoom(null, true, true);
            }
        }
    }


    onContainerReady() {
        this.#markLockVerticalDirty(true);
        this.#miniMapDirty = true;
        this.#observeContainer();
        if (!this.container || this.#grid) return;

        if (this.showGrid && this.type === "Perspective") {
            this.#setUpGrid();
        }
    }

    #setUpCamera() {

        var domElement = PlanogramRenderer.get(this).domElement;
        var size = { width: domElement.offsetWidth, height: domElement.offsetHeight };
        var newCamera = !this.#camera;

        var actuals = {
            lookat: new THREE.Vector3().copy(this.#movementControls?.target ?? new THREE.Vector3()),
            position: new THREE.Vector3().copy(this.#camera?.position ?? new THREE.Vector3()),
            zoom: this.#camera?.zoom ?? 1,
            fov: this.#camera ? this.#camera.fov : 50,
        }

        if (this.type === "Perspective") {
            this.#camera = new THREE.PerspectiveCamera(50, size.width / size.height, 0.1, this.perspectiveCameraMaxDistance);
            this.#camera.layers.enable(PlanogramRendererResources.PERSPECTIVE_LAYER);
            this.#camera.layers.disable(PlanogramRendererResources.ORTHOGRAPHIC_LAYER);
            if (this.#gridLayer !== null) {
                this.#camera.layers.enable(this.#gridLayer);
            }

        }
        else if (this.type === "Orthographic") {
            this.#camera = new THREE.OrthographicCamera(size.width / - 2, size.width / 2, size.height / 2, size.height / - 2, 1, 1000);
            this.#camera.layers.enable(PlanogramRendererResources.ORTHOGRAPHIC_LAYER);
            this.#camera.layers.disable(PlanogramRendererResources.PERSPECTIVE_LAYER);

        }

        if (this.#movementControls) {
            this.#movementControls.reset();
            this.#movementControls.object = this.#camera;
            this.#camera.position.copy(actuals.position);

            if (!newCamera) {
                if (this.#camera instanceof THREE.OrthographicCamera) {
                    var perspectiveHeight = 2 * this.#camera.position.distanceTo(actuals.lookat) * Math.tan(THREE.MathUtils.degToRad(actuals.fov / 2));
                    this.#camera.zoom = size.height / perspectiveHeight;
                } else {
                    var orthoHeight = size.height / actuals.zoom;
                    var distance = this.#camera.position.distanceTo(actuals.lookat);
                    var fov = 2 * THREE.MathUtils.radToDeg(Math.atan(orthoHeight / (2 * distance)));
                    this.#camera.fov = fov;
                }
            }

            this.#camera.updateProjectionMatrix();
            this.#movementControls.target = actuals.lookat;
            this.#movementControls.update();
        } else
            this.#updateMovementControls();

        if (newCamera)
            this.#setCameraPosition(true);

        this.syncLockVerticalLayout(newCamera);


    }

    #updateMovementControls() {
        if (!this.#movementControls) {
            var renderer = PlanogramRenderer.get(this);
            var domElement = renderer.domElement;
            this.#movementControls = new OrbitControls(this.#camera, domElement, this);
            this.#movementControls.target = this.cameraLookAt;
            this.#movementControls.update();
            this.#movementControls.mouseButtons.LEFT = null;
            this.#movementControls.addEventListener("change", this.#movementChangeListener);

            domElement.addEventListener("pointerdown", this.#pointerDown);
            domElement.addEventListener("pointerup", this.#pointerUp);
        }

        if (this.#usesLockedVerticalView()) {
            this.#movementControls.mouseButtons.RIGHT = THREE.MOUSE.PAN;
            this.#movementControls.enableRotate = false;
            this.#movementControls.enableZoom = false;
            this.#movementControls.touches.ONE = THREE.TOUCH.PAN;
            this.#movementControls.touches.TWO = THREE.TOUCH.DOLLY_PAN;
        }
        else if (this.control === "Dolly") {
            this.#movementControls.mouseButtons.RIGHT = THREE.MOUSE.PAN;
            this.#movementControls.enableRotate = false;
        }
        else if (this.control === "Orbit") {
            this.#movementControls.mouseButtons.RIGHT = THREE.MOUSE.ROTATE;
            this.#movementControls.enableRotate = true;
        }
        if (!this.#usesLockedVerticalView()) {
            this.#movementControls.enableZoom = !this.zoomDisabled;
            this.#movementControls.touches.ONE = THREE.TOUCH.ROTATE;
            this.#movementControls.touches.TWO = THREE.TOUCH.DOLLY_PAN;
        }
    }

    #usesLockedVerticalView() {
        return this.lockVertical && this.type === "Orthographic" && this.#camera instanceof THREE.OrthographicCamera;
    }

    #markLockVerticalDirty(resetPosition = false) {
        this.#lockVerticalDirty = true;
        if (resetPosition) {
            this.#lockVerticalInitialized = false;
        }
    }

    #observeContainer() {
        if (this.#containerObserver) {
            this.#containerObserver.disconnect();
            this.#containerObserver = null;
        }

        const container = this.container;
        if (!container || typeof MutationObserver === "undefined") return;

        this.#containerObserver = new MutationObserver(mutations => {
            let geometryChanged = false;
            let miniMapChanged = false;

            mutations.forEach(mutation => {
                if (mutation.type === "childList") {
                    geometryChanged = true;
                    miniMapChanged = true;
                    return;
                }

                if (mutation.attributeName === PlanogramObjectAttributes.Attributes.Selected) {
                    miniMapChanged = true;
                    return;
                }

                geometryChanged = true;
                miniMapChanged = true;
            });

            if (geometryChanged) {
                this.#markLockVerticalDirty();
            }
            if (miniMapChanged) {
                this.#miniMapDirty = true;
            }
        });
        this.#containerObserver.observe(container, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: PlanogramCamera.#miniMapObservedAttributes
        });
    }

    #getLockVerticalSceneBox() {
        const container = this.container;
        if (!container) return null;

        const boundingBox = container.boundingBox;
        if (boundingBox && !boundingBox.isEmpty()) {
            return boundingBox.clone();
        }

        const fallback = new THREE.Box3().setFromObject(container.group);
        return fallback.isEmpty() ? null : fallback;
    }

    #buildLockVerticalMetrics() {
        const dock = this.dockVector;
        const sceneBox = this.#getLockVerticalSceneBox();
        if (!dock || dock.z <= 0 || dock.w <= 0 || !sceneBox) return null;

        const sceneSize = sceneBox.getSize(new THREE.Vector3());
        const sceneHeight = Math.max(sceneSize.y, 0.0001);
        const sceneWidth = Math.max(sceneSize.x, 0);
        const zoom = dock.w / sceneHeight;
        const visibleWidth = dock.z / zoom;
        const halfVisibleWidth = visibleWidth / 2;
        const minCenterX = sceneBox.min.x + halfVisibleWidth;
        const maxCenterX = sceneBox.max.x - halfVisibleWidth;
        const sceneCenter = sceneBox.getCenter(new THREE.Vector3());
        const overflow = Math.max(0, sceneWidth - visibleWidth);
        const startCenterX = overflow > 0 ? minCenterX : sceneCenter.x;

        return {
            dock,
            zoom,
            centerY: sceneCenter.y,
            sceneCenterX: sceneCenter.x,
            sceneWidth,
            visibleWidth,
            minCenterX,
            maxCenterX,
            overflow,
            startCenterX
        };
    }

    #clampLockVerticalCenterX(value, metrics) {
        if (!metrics || metrics.overflow <= 0) {
            return metrics?.sceneCenterX ?? value;
        }
        return THREE.MathUtils.clamp(value, metrics.minCenterX, metrics.maxCenterX);
    }

    #getLockVerticalDepthOffset() {
        const currentOffset = this.#camera.position.z - this.#movementControls.target.z;
        if (Math.abs(currentOffset) > 0.0001) {
            return currentOffset;
        }

        const attributeOffset = this.cameraPosition.z - this.cameraLookAt.z;
        if (Math.abs(attributeOffset) > 0.0001) {
            return attributeOffset;
        }

        return this.orthoDistance;
    }

    #usesMiniMap() {
        return this.miniMap && this.type === "Orthographic" && this.#camera instanceof THREE.OrthographicCamera;
    }

    #ensureMiniMap() {
        if (this.#miniMapRoot) return this.#miniMapRoot;

        const host = this.renderer?.renderView;
        if (!host) return null;

        const root = document.createElement("div");
        root.className = "cameraMinimap hidden";

        const canvas = document.createElement("canvas");
        canvas.className = "cameraMinimapCanvas";
        const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.setClearColor(0x000000, 0);

        const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 5000);
        camera.layers.enable(PlanogramRendererResources.ORTHOGRAPHIC_LAYER);
        camera.layers.disable(PlanogramRendererResources.PERSPECTIVE_LAYER);
        camera.layers.enable(PlanogramRendererResources.DECORATOR_LAYER);

        const viewport = document.createElement("div");
        viewport.className = "cameraMinimapViewport";

        const resizeHandle = document.createElement("div");
        resizeHandle.className = "cameraMinimapResizeHandle";

        root.appendChild(canvas);
        root.appendChild(viewport);
        root.appendChild(resizeHandle);
        root.addEventListener("pointerdown", this.#miniMapPointerDownListener);
        root.addEventListener("pointermove", this.#miniMapPointerMoveListener);
        root.addEventListener("pointerup", this.#miniMapPointerUpListener);
        root.addEventListener("pointercancel", this.#miniMapPointerCancelListener);
        host.appendChild(root);

        this.#miniMapRoot = root;
        this.#miniMapCanvas = canvas;
        this.#miniMapRenderer = renderer;
        this.#miniMapCamera = camera;
        this.#miniMapViewport = viewport;
        this.#miniMapResizeHandle = resizeHandle;
        return root;
    }

    #hideMiniMap() {
        if (this.#miniMapRoot) {
            this.#miniMapRoot.classList.add("hidden");
        }
        this.#miniMapViewport?.classList.remove("dragging");
        this.#miniMapResizeHandle?.classList.remove("dragging");
    }

    #disposeMiniMap() {
        if (!this.#miniMapRoot) return;

        this.#miniMapRoot.removeEventListener("pointerdown", this.#miniMapPointerDownListener);
        this.#miniMapRoot.removeEventListener("pointermove", this.#miniMapPointerMoveListener);
        this.#miniMapRoot.removeEventListener("pointerup", this.#miniMapPointerUpListener);
        this.#miniMapRoot.removeEventListener("pointercancel", this.#miniMapPointerCancelListener);
        this.#miniMapRoot.remove();
        this.#miniMapRenderer?.dispose();
        this.#miniMapRenderer?.forceContextLoss?.();
        this.#miniMapRoot = null;
        this.#miniMapCanvas = null;
        this.#miniMapRenderer = null;
        this.#miniMapCamera = null;
        this.#miniMapViewport = null;
        this.#miniMapResizeHandle = null;
        this.#miniMapMetrics = null;
        this.#miniMapPointerId = null;
        this.#miniMapDragOffset = null;
        this.#miniMapDraggingViewport = false;
        this.#miniMapPointerMoved = false;
        this.#miniMapResizing = false;
        this.#miniMapResizeOrigin = null;
    }

    #resolveMiniMapSize(sceneWidth, sceneHeight, dock) {
        const minHeight = 96;
        const maxHeight = Math.min(Math.max(dock.w * 0.72, 200), 480);
        const minWidth = 160;
        const maxWidth = Math.min(Math.max(dock.z * 0.52, 200), 520);
        const defaultBox = {
            width: Math.min(Math.max(dock.z * 0.44, 200), maxWidth),
            height: Math.min(Math.max(dock.w * 0.56, 180), maxHeight)
        };
        const preferredBox = {
            width: THREE.MathUtils.clamp(this.#miniMapPreferredSize?.width ?? defaultBox.width, minWidth, maxWidth),
            height: THREE.MathUtils.clamp(this.#miniMapPreferredSize?.height ?? defaultBox.height, minHeight, maxHeight)
        };

        const minScale = Math.min(minWidth / sceneWidth, minHeight / sceneHeight);
        const maxScale = Math.min(maxWidth / sceneWidth, maxHeight / sceneHeight);
        const preferredScale = Math.min(preferredBox.width / sceneWidth, preferredBox.height / sceneHeight);
        const scale = THREE.MathUtils.clamp(preferredScale, Math.min(minScale, maxScale), maxScale);
        const rawMapWidth = sceneWidth * scale;
        const mapHeight = sceneHeight * scale;

        // Enforce a maximum aspect ratio to prevent overly wide minimaps for long planograms.
        // The cap is based on height so that wider scenes produce a narrower (but not huge) minimap.
        const maxMinimapAspectRatio = 8;
        const mapWidth = Math.min(rawMapWidth, Math.max(mapHeight * maxMinimapAspectRatio, minWidth));

        return {
            mapWidth,
            mapHeight,
            minWidth,
            maxWidth,
            minHeight,
            maxHeight
        };
    }

    #buildMiniMapMetrics() {
        const dock = this.dockVector;
        const sceneBox = this.#getLockVerticalSceneBox();
        if (!dock || dock.z <= 0 || dock.w <= 0 || !sceneBox || !(this.#camera instanceof THREE.OrthographicCamera)) return null;

        const sceneSize = sceneBox.getSize(new THREE.Vector3());
        const sceneWidth = Math.max(sceneSize.x, 0.0001);
        const sceneHeight = Math.max(sceneSize.y, 0.0001);
        const sceneCenter = sceneBox.getCenter(new THREE.Vector3());
        const zoom = Math.max(this.#camera.zoom, 0.0001);
        const visibleWidth = dock.z / zoom;
        const visibleHeight = dock.w / zoom;
        const size = this.#resolveMiniMapSize(sceneWidth, sceneHeight, dock);
        const mapWidth = size.mapWidth;
        const mapHeight = size.mapHeight;
        const renderZoom = Math.min((mapWidth * 0.92) / sceneWidth, (mapHeight * 0.92) / sceneHeight);

        return {
            dock,
            sceneBox,
            sceneWidth,
            sceneHeight,
            sceneCenter,
            visibleWidth,
            visibleHeight,
            mapWidth,
            mapHeight,
            minWidth: size.minWidth,
            maxWidth: size.maxWidth,
            minHeight: size.minHeight,
            maxHeight: size.maxHeight,
            renderZoom,
            minCenterX: sceneBox.min.x + (visibleWidth / 2),
            maxCenterX: sceneBox.max.x - (visibleWidth / 2),
            minCenterY: sceneBox.min.y + (visibleHeight / 2),
            maxCenterY: sceneBox.max.y - (visibleHeight / 2)
        };
    }

    #clampMiniMapCenter(value, min, max, fallback) {
        if (max < min) {
            return fallback;
        }
        return THREE.MathUtils.clamp(value, min, max);
    }

    #getMiniMapCurrentCenter(metrics) {
        const currentTarget = this.#movementControls.target;
        return new THREE.Vector2(
            this.#clampMiniMapCenter(currentTarget.x, metrics.minCenterX, metrics.maxCenterX, metrics.sceneCenter.x),
            this.#usesLockedVerticalView()
                ? metrics.sceneCenter.y
                : this.#clampMiniMapCenter(currentTarget.y, metrics.minCenterY, metrics.maxCenterY, metrics.sceneCenter.y)
        );
    }

    #miniMapWorldToPoint(metrics, x, y) {
        return new THREE.Vector2(
            ((x - metrics.sceneBox.min.x) / metrics.sceneWidth) * metrics.mapWidth,
            ((metrics.sceneBox.max.y - y) / metrics.sceneHeight) * metrics.mapHeight
        );
    }

    #miniMapEventToWorld(metrics, event) {
        if (!this.#miniMapRoot) return null;

        const rect = this.#miniMapRoot.getBoundingClientRect();
        if (!rect.width || !rect.height) return null;

        const offsetX = THREE.MathUtils.clamp(event.clientX - rect.left, 0, rect.width);
        const offsetY = THREE.MathUtils.clamp(event.clientY - rect.top, 0, rect.height);

        return new THREE.Vector2(
            metrics.sceneBox.min.x + ((offsetX / rect.width) * metrics.sceneWidth),
            metrics.sceneBox.max.y - ((offsetY / rect.height) * metrics.sceneHeight)
        );
    }

    #renderMiniMap(metrics) {
        const renderer = this.#miniMapRenderer;
        const camera = this.#miniMapCamera;
        const container = this.container;
        if (!renderer || !camera || !container) return;

        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        renderer.setPixelRatio(pixelRatio);
        renderer.setSize(metrics.mapWidth, metrics.mapHeight, false);

        camera.left = metrics.mapWidth / -2;
        camera.right = metrics.mapWidth / 2;
        camera.top = metrics.mapHeight / 2;
        camera.bottom = metrics.mapHeight / -2;
        camera.zoom = metrics.renderZoom;
        camera.position.set(metrics.sceneCenter.x, metrics.sceneCenter.y, this.#movementControls.target.z + this.#getLockVerticalDepthOffset());
        camera.lookAt(new THREE.Vector3(metrics.sceneCenter.x, metrics.sceneCenter.y, this.#movementControls.target.z));
        camera.updateProjectionMatrix();

        renderer.clear();
        renderer.render(container.scene, camera);
        this.#miniMapDirty = false;
    }

    #syncMiniMapViewport(metrics) {
        if (!this.#miniMapViewport) return;

        const center = this.#getMiniMapCurrentCenter(metrics);
        const viewportWidth = Math.min((metrics.visibleWidth / metrics.sceneWidth) * metrics.mapWidth, metrics.mapWidth);
        const viewportHeight = Math.min((metrics.visibleHeight / metrics.sceneHeight) * metrics.mapHeight, metrics.mapHeight);
        const topLeft = this.#miniMapWorldToPoint(metrics, center.x - (metrics.visibleWidth / 2), center.y + (metrics.visibleHeight / 2));
        const left = THREE.MathUtils.clamp(topLeft.x, 0, Math.max(metrics.mapWidth - viewportWidth, 0));
        const top = THREE.MathUtils.clamp(topLeft.y, 0, Math.max(metrics.mapHeight - viewportHeight, 0));

        this.#miniMapViewport.style.left = `${left}px`;
        this.#miniMapViewport.style.top = `${top}px`;
        this.#miniMapViewport.style.width = `${Math.max(viewportWidth, 8)}px`;
        this.#miniMapViewport.style.height = `${Math.max(viewportHeight, 8)}px`;
    }

    #applyMiniMapCenter(metrics, centerX, centerY, persistState = false, raiseEvent = false) {
        if (!(this.#camera instanceof THREE.OrthographicCamera)) return;

        const clampedX = this.#clampMiniMapCenter(centerX, metrics.minCenterX, metrics.maxCenterX, metrics.sceneCenter.x);
        const clampedY = this.#usesLockedVerticalView()
            ? metrics.sceneCenter.y
            : this.#clampMiniMapCenter(centerY, metrics.minCenterY, metrics.maxCenterY, metrics.sceneCenter.y);
        const targetZ = this.#movementControls.target.z;
        const target = new THREE.Vector3(clampedX, clampedY, targetZ);
        const position = new THREE.Vector3(clampedX, clampedY, targetZ + this.#getLockVerticalDepthOffset());

        this.#camera.position.copy(position);
        this.#movementControls.target.copy(target);
        this.#camera.updateProjectionMatrix();
        this.#camera.lookAt(target);

        if (this.#usesLockedVerticalView() && this.#lockVerticalMetrics) {
            this.#updateLockVerticalScrollbar(this.#lockVerticalMetrics, clampedX);
        }

        if (persistState) {
            this.#writeCameraStateToAttributes(raiseEvent);
        }
    }

    #handleMiniMapPointerDown(event) {
        if (!this.#usesMiniMap() || !this.#miniMapMetrics || event.button !== 0) return;

        event.preventDefault();
        event.stopPropagation();

        const path = typeof event.composedPath === "function" ? event.composedPath() : [];
        const hitResizeHandle = this.#miniMapResizeHandle && (event.target === this.#miniMapResizeHandle || path.includes(this.#miniMapResizeHandle));
        const hitViewport = this.#miniMapViewport && (event.target === this.#miniMapViewport || path.includes(this.#miniMapViewport));

        if (hitResizeHandle && this.#miniMapRoot) {
            this.#miniMapPointerId = event.pointerId;
            this.#miniMapResizing = true;
            this.#miniMapPointerMoved = false;
            this.#miniMapResizeOrigin = {
                clientX: event.clientX,
                clientY: event.clientY,
                right: this.#miniMapRoot.getBoundingClientRect().right,
                bottom: this.#miniMapRoot.getBoundingClientRect().bottom
            };
            this.#miniMapResizeHandle.classList.add("dragging");
            this.#miniMapRoot.setPointerCapture(event.pointerId);
            return;
        }

        const worldPoint = this.#miniMapEventToWorld(this.#miniMapMetrics, event);
        if (!worldPoint) return;

        if (hitViewport && this.#miniMapRoot) {
            this.#miniMapPointerId = event.pointerId;
            this.#miniMapDraggingViewport = true;
            this.#miniMapPointerMoved = false;
            this.#miniMapDragOffset = worldPoint.sub(this.#getMiniMapCurrentCenter(this.#miniMapMetrics));
            this.#miniMapViewport?.classList.add("dragging");
            this.#miniMapRoot.setPointerCapture(event.pointerId);
            return;
        }

        this.#applyMiniMapCenter(this.#miniMapMetrics, worldPoint.x, worldPoint.y, true, true);
        this.syncMiniMapLayout();
    }

    #handleMiniMapPointerMove(event) {
        if (this.#miniMapResizing && this.#miniMapPointerId === event.pointerId && this.#miniMapResizeOrigin) {
            event.preventDefault();
            event.stopPropagation();

            this.#miniMapPreferredSize = {
                width: this.#miniMapResizeOrigin.right - event.clientX,
                height: this.#miniMapResizeOrigin.bottom - event.clientY
            };
            this.#miniMapPointerMoved = true;
            this.syncMiniMapLayout();
            return;
        }

        if (!this.#miniMapDraggingViewport || this.#miniMapPointerId !== event.pointerId || !this.#miniMapMetrics) return;

        event.preventDefault();
        event.stopPropagation();

        const worldPoint = this.#miniMapEventToWorld(this.#miniMapMetrics, event);
        if (!worldPoint) return;

        this.#miniMapPointerMoved = true;
        const dragOffset = this.#miniMapDragOffset ?? new THREE.Vector2();
        this.#applyMiniMapCenter(this.#miniMapMetrics, worldPoint.x - dragOffset.x, worldPoint.y - dragOffset.y);
        this.syncMiniMapLayout();
    }

    #completeMiniMapPointerInteraction(event, persistState) {
        if (this.#miniMapPointerId !== event.pointerId) return;

        event.preventDefault();
        event.stopPropagation();

        if (this.#miniMapRoot?.hasPointerCapture(event.pointerId)) {
            this.#miniMapRoot.releasePointerCapture(event.pointerId);
        }

        if (this.#miniMapResizing) {
            if (this.#miniMapMetrics) {
                this.#miniMapPreferredSize = {
                    width: THREE.MathUtils.clamp(this.#miniMapPreferredSize?.width ?? this.#miniMapMetrics.mapWidth, this.#miniMapMetrics.minWidth, this.#miniMapMetrics.maxWidth),
                    height: THREE.MathUtils.clamp(this.#miniMapPreferredSize?.height ?? this.#miniMapMetrics.mapHeight, this.#miniMapMetrics.minHeight, this.#miniMapMetrics.maxHeight)
                };
            }
            this.#miniMapResizing = false;
            this.#miniMapResizeOrigin = null;
            this.#miniMapResizeHandle?.classList.remove("dragging");
        }

        if (persistState && this.#miniMapMetrics && this.#miniMapDraggingViewport) {
            const center = this.#getMiniMapCurrentCenter(this.#miniMapMetrics);
            this.#applyMiniMapCenter(this.#miniMapMetrics, center.x, center.y, true, this.#miniMapPointerMoved);
        }

        this.#miniMapPointerId = null;
        this.#miniMapDragOffset = null;
        this.#miniMapDraggingViewport = false;
        this.#miniMapPointerMoved = false;
        this.#miniMapViewport?.classList.remove("dragging");
    }

    #handleMiniMapPointerUp(event) {
        this.#completeMiniMapPointerInteraction(event, true);
    }

    #handleMiniMapPointerCancel(event) {
        this.#completeMiniMapPointerInteraction(event, false);
    }

    syncMiniMapLayout() {
        if (!this.#usesMiniMap() || !this.active) {
            this.#miniMapMetrics = null;
            this.#hideMiniMap();
            return;
        }

        const metrics = this.#buildMiniMapMetrics();
        this.#miniMapMetrics = metrics;
        if (!metrics) {
            this.#hideMiniMap();
            return;
        }

        const minimap = this.#ensureMiniMap();
        if (!minimap || !this.#miniMapCanvas) return;

        const margin = 12;
        const bottomInset = this.#usesLockedVerticalView() && this.#lockVerticalMetrics?.overflow > 0 ? 32 : margin;
        const left = Math.max(metrics.dock.x + metrics.dock.z - metrics.mapWidth - margin, metrics.dock.x + 4);
        const top = Math.max(metrics.dock.y + metrics.dock.w - metrics.mapHeight - bottomInset, metrics.dock.y + 4);

        minimap.style.left = `${left}px`;
        minimap.style.top = `${top}px`;
        minimap.style.width = `${metrics.mapWidth}px`;
        minimap.style.height = `${metrics.mapHeight}px`;

        this.#renderMiniMap(metrics);

        this.#syncMiniMapViewport(metrics);
        minimap.classList.remove("hidden");
    }

    #ensureLockVerticalScrollbar() {
        if (this.#lockVerticalScrollbar) return this.#lockVerticalScrollbar;

        const host = this.renderer?.renderView;
        if (!host) return null;

        const scrollbar = document.createElement("div");
        scrollbar.className = "cameraScrollbar hidden";
        const spacer = document.createElement("div");
        spacer.className = "cameraScrollbarSpacer";
        scrollbar.appendChild(spacer);

        scrollbar.addEventListener("scroll", this.#lockVerticalScrollListener);
        scrollbar.addEventListener("pointerdown", this.#stopScrollEvent);
        scrollbar.addEventListener("pointermove", this.#stopScrollEvent);
        scrollbar.addEventListener("wheel", this.#stopScrollEvent);
        host.appendChild(scrollbar);
        this.#lockVerticalScrollbar = scrollbar;
        this.#lockVerticalScrollbarSpacer = spacer;
        return scrollbar;
    }

    #hideLockVerticalScrollbar() {
        if (this.#lockVerticalScrollbar) {
            this.#lockVerticalScrollbar.classList.add("hidden");
        }
    }

    #disposeLockVerticalScrollbar() {
        if (!this.#lockVerticalScrollbar) return;

        this.#lockVerticalScrollbar.removeEventListener("scroll", this.#lockVerticalScrollListener);
        this.#lockVerticalScrollbar.removeEventListener("pointerdown", this.#stopScrollEvent);
        this.#lockVerticalScrollbar.removeEventListener("pointermove", this.#stopScrollEvent);
        this.#lockVerticalScrollbar.removeEventListener("wheel", this.#stopScrollEvent);
        this.#lockVerticalScrollbar.remove();
        this.#lockVerticalScrollbar = null;
        this.#lockVerticalScrollbarSpacer = null;
        this.#lockVerticalScrollbarSyncing = false;
        this.#lockVerticalExpectedScrollLeft = null;
    }

    #updateLockVerticalScrollbar(metrics, centerX) {
        const scrollbar = this.#ensureLockVerticalScrollbar();
        const spacer = this.#lockVerticalScrollbarSpacer;
        if (!scrollbar || !spacer || !metrics || metrics.overflow <= 0 || !this.active) {
            this.#hideLockVerticalScrollbar();
            return;
        }

        const margin = 12;
        const width = Math.max(metrics.dock.z - (margin * 2), 48);
        const height = 16;
        const top = Math.max(metrics.dock.y + metrics.dock.w - height, metrics.dock.y + 4);
        const span = metrics.maxCenterX - metrics.minCenterX;
        const ratio = span <= 0 ? 0 : (centerX - metrics.minCenterX) / span;
        const contentWidth = Math.max(width * (metrics.sceneWidth / Math.max(metrics.visibleWidth, 0.0001)), width + 1);

        scrollbar.style.left = `${metrics.dock.x + margin}px`;
        scrollbar.style.top = `${top}px`;
        scrollbar.style.width = `${width}px`;
        scrollbar.style.height = `${height}px`;
        spacer.style.width = `${contentWidth}px`;

        const maxScrollLeft = Math.max(contentWidth - width, 0);
        const scrollLeft = THREE.MathUtils.clamp(ratio, 0, 1) * maxScrollLeft;
        if (Math.abs(scrollbar.scrollLeft - scrollLeft) > 0.5) {
            this.#lockVerticalExpectedScrollLeft = scrollLeft;
            this.#lockVerticalScrollbarSyncing = true;
            scrollbar.scrollLeft = scrollLeft;
            this.#lockVerticalScrollbarSyncing = false;
        } else {
            this.#lockVerticalExpectedScrollLeft = null;
        }
        scrollbar.classList.remove("hidden");
    }

    #writeCameraStateToAttributes(raiseEvent = false) {
        const position = this.#camera.position;
        const lookAt = this.#movementControls.target;

        this.#writingLockVerticalState = true;
        this.setAttribute(PlanogramCamera.Attributes.PositionX, position.x);
        this.setAttribute(PlanogramCamera.Attributes.PositionY, position.y);
        this.setAttribute(PlanogramCamera.Attributes.PositionZ, position.z);
        this.setAttribute(PlanogramCamera.Attributes.LookAtX, lookAt.x);
        this.setAttribute(PlanogramCamera.Attributes.LookAtY, lookAt.y);
        this.setAttribute(PlanogramCamera.Attributes.LookAtZ, lookAt.z);
        this.#writingLockVerticalState = false;

        if (raiseEvent) {
            this.raiseMoveEvent(position.clone(), lookAt.clone());
        }
    }

    #applyLockVerticalMetrics(metrics, resetHorizontal = false, forcedCenterX = null, persistState = false) {
        if (!this.#usesLockedVerticalView() || !metrics) return;

        const currentTarget = this.#movementControls.target.clone();
        const targetX = forcedCenterX ?? (resetHorizontal ? metrics.startCenterX : this.#clampLockVerticalCenterX(currentTarget.x, metrics));
        const target = new THREE.Vector3(targetX, metrics.centerY, currentTarget.z);
        const position = new THREE.Vector3(target.x, target.y, target.z + this.#getLockVerticalDepthOffset());

        this.#camera.zoom = metrics.zoom;
        this.#camera.position.copy(position);
        this.#movementControls.target.copy(target);
        this.#camera.updateProjectionMatrix();
        this.#camera.lookAt(target);
        this.#updateLockVerticalScrollbar(metrics, target.x);

        if (persistState) {
            this.#writeCameraStateToAttributes();
        }
    }

    #handleMovementControlsChange() {
        if (!this.#usesLockedVerticalView()) {
            this.#hideLockVerticalScrollbar();
        } else {
            this.syncLockVerticalLayout();
        }

        this.syncMiniMapLayout();
    }

    #handleLockVerticalScrollbarScroll(event) {
        if (!this.#usesLockedVerticalView() || !this.#lockVerticalMetrics || this.#lockVerticalScrollbarSyncing) return;

        const scrollbar = event.currentTarget;
        if (!(scrollbar instanceof HTMLDivElement)) return;

        if (this.#lockVerticalExpectedScrollLeft !== null && Math.abs(scrollbar.scrollLeft - this.#lockVerticalExpectedScrollLeft) <= 0.5) {
            this.#lockVerticalExpectedScrollLeft = null;
            return;
        }

        this.#lockVerticalExpectedScrollLeft = null;

        const maxScrollLeft = Math.max(scrollbar.scrollWidth - scrollbar.clientWidth, 0);
        const span = this.#lockVerticalMetrics.maxCenterX - this.#lockVerticalMetrics.minCenterX;
        const ratio = maxScrollLeft <= 0 ? 0 : scrollbar.scrollLeft / maxScrollLeft;
        const centerX = span <= 0 ? this.#lockVerticalMetrics.sceneCenterX : this.#lockVerticalMetrics.minCenterX + (span * ratio);

        this.#applyLockVerticalMetrics(this.#lockVerticalMetrics, false, centerX, true);
    }

    syncLockVerticalLayout(resetHorizontal = false) {
        if (!this.#usesLockedVerticalView() || !this.active) {
            this.#lockVerticalMetrics = null;
            this.#lockVerticalDockKey = null;
            this.#lockVerticalInitialized = false;
            this.#hideLockVerticalScrollbar();
            return;
        }

        const dock = this.dockVector;
        if (!dock || dock.z <= 0 || dock.w <= 0) {
            this.#hideLockVerticalScrollbar();
            return;
        }

        const dockKey = `${dock.x}:${dock.y}:${dock.z}:${dock.w}`;
        if (this.#lockVerticalDirty || !this.#lockVerticalMetrics || this.#lockVerticalDockKey !== dockKey) {
            this.#lockVerticalMetrics = this.#buildLockVerticalMetrics();
            this.#lockVerticalDockKey = dockKey;
        }

        if (!this.#lockVerticalMetrics) {
            this.#hideLockVerticalScrollbar();
            return;
        }

        this.#applyLockVerticalMetrics(this.#lockVerticalMetrics, resetHorizontal || !this.#lockVerticalInitialized);
        this.#lockVerticalDirty = false;
        this.#lockVerticalInitialized = true;
    }

    panByViewportPixels(deltaX, deltaY, persistState = false, raiseEvent = false) {
        if (!this.#camera || !this.#movementControls || !this.active) return false;

        const dock = this.dockVector;
        if (!dock || dock.z <= 0 || dock.w <= 0) return false;

        const horizontalDelta = Number.isFinite(deltaX) ? deltaX : 0;
        const verticalDelta = this.#usesLockedVerticalView() || !Number.isFinite(deltaY) ? 0 : deltaY;
        if (Math.abs(horizontalDelta) < 0.001 && Math.abs(verticalDelta) < 0.001) return false;

        if (this.#usesLockedVerticalView()) {
            if (!this.#lockVerticalMetrics) {
                this.#lockVerticalMetrics = this.#buildLockVerticalMetrics();
            }

            if (!this.#lockVerticalMetrics) return false;

            const viewWidth = (this.#camera.right - this.#camera.left) / Math.max(this.#camera.zoom, 0.0001);
            const worldDeltaX = (horizontalDelta * viewWidth) / dock.z;
            const nextCenterX = this.#movementControls.target.x + worldDeltaX;

            this.#applyLockVerticalMetrics(this.#lockVerticalMetrics, false, nextCenterX, persistState);
            this.syncMiniMapLayout();
            if (persistState && raiseEvent) {
                this.raiseMoveEvent(this.#camera.position.clone(), this.#movementControls.target.clone());
            }
            return true;
        }

        const right = new THREE.Vector3().setFromMatrixColumn(this.#camera.matrix, 0);
        const up = this.#movementControls.screenSpacePanning === true
            ? new THREE.Vector3().setFromMatrixColumn(this.#camera.matrix, 1)
            : new THREE.Vector3().setFromMatrixColumn(this.#camera.matrix, 0).cross(this.#camera.up).normalize();

        let worldDeltaX = 0;
        let worldDeltaY = 0;

        if (this.#camera instanceof THREE.PerspectiveCamera) {
            const offset = this.#camera.position.clone().sub(this.#movementControls.target);
            const targetDistance = offset.length() * Math.tan((this.#camera.fov / 2) * Math.PI / 180.0);
            worldDeltaX = (2 * horizontalDelta * targetDistance) / dock.w;
            worldDeltaY = (2 * verticalDelta * targetDistance) / dock.w;
        } else if (this.#camera instanceof THREE.OrthographicCamera) {
            const zoom = Math.max(this.#camera.zoom, 0.0001);
            worldDeltaX = (horizontalDelta * (this.#camera.right - this.#camera.left)) / zoom / dock.z;
            worldDeltaY = (verticalDelta * (this.#camera.top - this.#camera.bottom)) / zoom / dock.w;
        } else {
            return false;
        }

        const translation = right.multiplyScalar(worldDeltaX).add(up.multiplyScalar(-worldDeltaY));
        if (translation.lengthSq() <= 0.000001) return false;

        this.#camera.position.add(translation);
        this.#movementControls.target.add(translation);
        this.#camera.updateProjectionMatrix();
        this.#camera.lookAt(this.#movementControls.target);

        if (persistState) {
            this.#writeCameraStateToAttributes(raiseEvent);
        }

        this.syncMiniMapLayout();
        return true;
    }

    #setUpGrid() {
        if (this.type !== "Perspective" || !this.showGrid) {
            this.#disposeGrid();
            if (this.#movementControls) {
                this.#movementControls.removeEventListener("change", this.#gridChangeListener);
            }
            return;
        }

        const container = this.container;
        if (!container) return;

        if (this.#movementControls) {
            this.#movementControls.addEventListener("change", this.#gridChangeListener);
        }

        const camera = this.#camera;
        const distance = camera.position.distanceTo(new THREE.Vector3());
        const size = distance * 3;
        const unitSize = 10;
        const gridVertices = [];
        for (let i = -size; i <= size; i += unitSize) {
            gridVertices.push(-size, 0, i, size, 0, i);
            gridVertices.push(i, 0, -size, i, 0, size);
        }
        const attribute = new THREE.Float32BufferAttribute(gridVertices, 3);

        if (!this.#grid) {
            this.#gridLayer = this.#allocateGridLayer();
            if (this.#gridLayer === null) {
                console.warn('No available grid layers. Maximum number of cameras with grids reached.');
                return;
            }

            this.#grid = new THREE.LineSegments(
                new THREE.BufferGeometry().setAttribute('position', attribute),
                new THREE.LineBasicMaterial({ color: new THREE.Color(0x888888), transparent: true, opacity: 0.3 })
            );
            this.#grid.layers.set(this.#gridLayer);
            container.scene.add(this.#grid);

            this.#camera.layers.enable(this.#gridLayer);
        } else {
            this.#grid.geometry.setAttribute('position', attribute);
        }

        const midPointPosition = -size + (Math.ceil(size / unitSize) * unitSize);
        this.#grid.position.set(-midPointPosition, 0, -midPointPosition);
    }

    /**
     * Allocate a unique grid layer for this camera
     * @returns {number|null} The allocated layer number, or null if none available
     */
    #allocateGridLayer() {
        if (PlanogramCamera.#recycledGridLayers.length > 0) {
            return PlanogramCamera.#recycledGridLayers.pop();
        }

        if (PlanogramCamera.#nextGridLayer <= PlanogramCamera.#MAX_GRID_LAYER) {
            return PlanogramCamera.#nextGridLayer++;
        }

        return null;
    }

    /**
     * Recycle a grid layer so it can be reused by another camera
     * @param {number} layer The layer number to recycle
     */
    #recycleGridLayer(layer) {
        if (layer !== null && layer >= 10 && layer <= PlanogramCamera.#MAX_GRID_LAYER) {
            if (!PlanogramCamera.#recycledGridLayers.includes(layer)) {
                PlanogramCamera.#recycledGridLayers.push(layer);
            }
        }
    }

    #disposeGrid() {
        if (this.#grid) {
            const container = this.container;
            if (container && container.scene) {
                container.scene.remove(this.#grid);
            }
            this.#grid.geometry.dispose();
            this.#grid.material.dispose();
            this.#grid = null;

            if (this.#gridLayer !== null && this.#camera) {
                this.#camera.layers.disable(this.#gridLayer);
                this.#recycleGridLayer(this.#gridLayer);
                this.#gridLayer = null;
            }
        }
    }

    #removeListeners() {
        const domElement = this.renderer.domElement;
        domElement.removeEventListener("pointerdown", this.#pointerDown);
        domElement.removeEventListener("pointerup", this.#pointerUp);

        if (this.#movementControls) {
            this.#movementControls.removeEventListener("change", this.#gridChangeListener);
        }

        // Remove Blazor relay event listeners
        if (Reflect.has(document, "blazorRelay")) {
            const values = Object.values(PlanogramCamera.customEvents);
            for (const value of values) {
                this.removeEventListener(value, document.blazorRelay.relayToInput);
            }
        }
    }

    #setCameraPosition(newCamera = false) {

        if (newCamera) {
            if (!this.defaultPosition)
                this.setAttribute(PlanogramCamera.Attributes.DefaultPosition, `${this.positionX},${this.positionY},${this.positionZ}`);
            if (!this.defaultLookAt)
                this.setAttribute(PlanogramCamera.Attributes.DefaultLookAt, `${this.lookAtX},${this.lookAtY},${this.lookAtZ}`);
            this.#defaultZoom = this.#camera.zoom;
        }

        this.#camera.position.copy(this.cameraPosition);
        this.#movementControls.target.copy(this.cameraLookAt);
        this.#camera.updateProjectionMatrix();
        this.#movementControls.update();
        this.syncLockVerticalLayout();
    }

    /**
     * 
     * @param {number} x 
     * @param {number} y 
     * @param {number} z 
     * @param {number} width 
     * @param {number} height 
     * @param {number} depth 
     */
    updateZoomFromCentreAndSize(x, y, z, width, height, depth, updateLookAt = false) {
        const box = new THREE.Box3().setFromCenterAndSize(new THREE.Vector3(x, y, z), new THREE.Vector3(width, height, depth));
        this.updateZoom(box, updateLookAt);
    }

    resetAndFit() {
        this.resetCamera();
        this.updateZoom(null, true);
    }

    /**
     * 
     * @param {THREE.Box3} box 
     * @param {boolean} updateLookAt
     * @param {boolean} suppressEvent - If true, do not dispatch camera move event
     */
    updateZoom(box, updateLookAt = false, suppressEvent = false) {
        if (!box) {
            // Build a tighter box from positions/components only to avoid labels/overlays skewing the center
            const container = this.container;
            if (!container) return;
            const targets = [...container.querySelectorAll("planogram-position, planogram-component")];
            if (targets.length) {
                const tmp = new THREE.Box3();
                targets.forEach(t => { if (t.group) tmp.expandByObject(t.group); });
                box = tmp.isEmpty() ? new THREE.Box3().setFromObject(container.group) : tmp;
            } else {
                box = new THREE.Box3().setFromObject(container.group);
            }
        }

        // Force layout calculation if needed
        let dock = this.dockVector;
        if (!dock || dock.z === 0 || dock.w === 0) {
            const frame = this.cameraFrame;
            if (frame) {
                // Force a synchronous layout recalculation
                frame.getBoundingClientRect();
                dock = this.dockVector;
            }
        }

        // If still not ready, schedule one microtask retry (faster than requestAnimationFrame)
        if (box.isEmpty() || !dock || dock.z === 0 || dock.w === 0) {
            if (!suppressEvent) {
                // Only retry on user-initiated zooms, not auto-zoom
                queueMicrotask(() => this.updateZoom(box, updateLookAt, suppressEvent));
            }
            return;
        }
        const centre = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const lookatDiff = centre.clone().sub(this.#movementControls.target);

        if (this.#camera instanceof THREE.PerspectiveCamera) {

            // Fit world-aligned box using tan-based FOV math (more stable than sphere fit)
            const aspect = (this.dockVector?.z && this.dockVector?.w) ? (this.dockVector.z / this.dockVector.w) : (this.#camera.aspect || 1);
            const vFOV = THREE.MathUtils.degToRad(this.#camera.fov);
            const hFOV = 2 * Math.atan(Math.tan(vFOV / 2) * aspect);
            const pad = 1.05; // 5% padding to avoid tight edges
            const distY = (size.y * pad) / 2 / Math.tan(vFOV / 2);
            const distX = (size.x * pad) / 2 / Math.tan(hFOV / 2);
            const baseDistance = Math.max(distX, distY) + (size.z * 0.5);

            // Move camera along its current viewing direction to maintain orientation
            const viewDir = this.#camera.position.clone().sub(this.#movementControls.target).normalize();
            const newPosition = centre.clone().add(viewDir.multiplyScalar(baseDistance));

            this.#camera.near = Math.max(baseDistance / 100, 0.1);
            this.#camera.far = Math.max(baseDistance * 100, this.#camera.near + 1e-3);

            if (newPosition.equals(centre)) {
                this.resetCamera();
                this.updateZoom(null, updateLookAt, suppressEvent);
            } else {
                if (updateLookAt) {
                    this.setAttribute(PlanogramCamera.Attributes.LookAtX, centre.x);
                    this.setAttribute(PlanogramCamera.Attributes.LookAtY, centre.y);
                    this.setAttribute(PlanogramCamera.Attributes.LookAtZ, centre.z);
                }
                this.setAttribute(PlanogramCamera.Attributes.PositionX, newPosition.x);
                this.setAttribute(PlanogramCamera.Attributes.PositionY, newPosition.y);
                this.setAttribute(PlanogramCamera.Attributes.PositionZ, newPosition.z);
                if (!suppressEvent) {
                    this.raiseMoveEvent(newPosition, centre);
                }
            }
            // this.#camera.position.copy(this.#movementControls.target).sub(direction);
        }
        else if (this.#camera instanceof THREE.OrthographicCamera) {

            this.#camera.updateProjectionMatrix();
            this.#camera.updateMatrixWorld();
            const corners = [
                new THREE.Vector3(box.min.x, box.min.y, box.min.z),
                new THREE.Vector3(box.min.x, box.min.y, box.max.z),
                new THREE.Vector3(box.min.x, box.max.y, box.min.z),
                new THREE.Vector3(box.min.x, box.max.y, box.max.z),
                new THREE.Vector3(box.max.x, box.min.y, box.min.z),
                new THREE.Vector3(box.max.x, box.min.y, box.max.z),
                new THREE.Vector3(box.max.x, box.max.y, box.min.z),
                new THREE.Vector3(box.max.x, box.max.y, box.max.z),
            ];
            // Use a temporary view matrix aimed at the final center so the fit matches final orientation
            const viewMatrix = updateLookAt
                ? new THREE.Matrix4().lookAt(this.#camera.position, centre, this.#camera.up)
                : this.#camera.matrixWorldInverse;
            corners.forEach(c => c.applyMatrix4(viewMatrix));
            const camBox = new THREE.Box3().setFromPoints(corners);
            const size = camBox.getSize(new THREE.Vector3());

            this.#camera.left = this.dockVector.z / -2;
            this.#camera.right = this.dockVector.z / 2;
            this.#camera.top = this.dockVector.w / 2;
            this.#camera.bottom = this.dockVector.w / -2;

            const fitX = (this.#camera.right - this.#camera.left) / size.x;
            const fitY = (this.#camera.top - this.#camera.bottom) / size.y;
            const padding = 0.95; // leave a small headroom so content doesn't touch edges
            const newZoom = Math.min(fitX, fitY) * padding;
            if (newZoom !== 0)
                this.#camera.zoom = newZoom;
            if (updateLookAt) {
                var newPosition = this.#camera.position.clone().add(lookatDiff);
                this.setAttribute(PlanogramCamera.Attributes.PositionX, newPosition.x);
                this.setAttribute(PlanogramCamera.Attributes.PositionY, newPosition.y);
                this.setAttribute(PlanogramCamera.Attributes.PositionZ, newPosition.z);
                this.setAttribute(PlanogramCamera.Attributes.LookAtX, centre.x);
                this.setAttribute(PlanogramCamera.Attributes.LookAtY, centre.y);
                this.setAttribute(PlanogramCamera.Attributes.LookAtZ, centre.z);
                if (!suppressEvent) {
                    this.raiseMoveEvent(newPosition, centre);
                }
            }
        }
        this.#camera.updateProjectionMatrix();
        this.#movementControls.update();
    }

    zoomToSelection(showAll = false) {
        /**@type {PlanogramObject[]} */
        var selectedObjects = [...this.querySelectorAll("[selected]")];
        var box = new THREE.Box3();
        if (selectedObjects.length > 0 && !showAll) {
            box = selectedObjects.reduce((a, c) => {
                box.expandByObject(c.group);
                return a;
            }, box);
        } else {
            box = box.setFromObject(this.container.group);
        }
        this.updateZoom(box);
    }

    resetCamera() {

        this.setAttribute(PlanogramCamera.Attributes.PositionX, 0);
        this.setAttribute(PlanogramCamera.Attributes.PositionY, 0);
        this.setAttribute(PlanogramCamera.Attributes.PositionZ, 0);
        this.setAttribute(PlanogramCamera.Attributes.LookAtX, 0);
        this.setAttribute(PlanogramCamera.Attributes.LookAtY, 0);
        this.setAttribute(PlanogramCamera.Attributes.LookAtZ, 0);

        if (this.defaultPosition) {
            var { x, y, z } = this.defaultPosition;
            this.setAttribute(PlanogramCamera.Attributes.PositionX, x);
            this.setAttribute(PlanogramCamera.Attributes.PositionY, y);
            this.setAttribute(PlanogramCamera.Attributes.PositionZ, z);
        }
        if (this.defaultLookAt) {
            var { x, y, z } = this.defaultLookAt;
            this.setAttribute(PlanogramCamera.Attributes.LookAtX, x);
            this.setAttribute(PlanogramCamera.Attributes.LookAtY, y);
            this.setAttribute(PlanogramCamera.Attributes.LookAtZ, z);
        }

    }

    #disposeMovementControls() {
        if (this.#movementControls) {
            this.#movementControls.removeEventListener("change", this.#movementChangeListener);
            this.#movementControls.dispose();
            this.#movementControls = null;
        }
    }
    //#endregion

    //#region Tab Item Methods

    #setUpTabItem() {

        if (this.groupFrameId) {
            this.#tabItem?.remove();
            this.#tabItem = null;
            return;
        }

        const topHighlight = "highlight-top";
        const bottomHighlight = "highlight-bottom";
        const leftHighlight = "highlight-left";
        const rightHighlight = "highlight-right";
        const dragThreshold = 5;
        const tabItem = document.createElement("div");
        tabItem.classList.add("tabItem");
        tabItem.id = this.uid;
        tabItem.addEventListener("pointerdown", down => {
            if (this.#closebutton && this.#closebutton.contains(down.target)) return;

            // Check if tab dragging is disabled
            if (this.lockTab || (this.renderer && this.renderer.disableTabDrag)) {
                // Still allow click/focus events
                const clickHandler = up => {
                    tabItem.removeEventListener("pointerup", clickHandler);
                    if (Math.abs(down.clientX - up.clientX) < dragThreshold && Math.abs(down.clientY - up.clientY) < dragThreshold) {
                        if (!this.active)
                            this.raiseClickEvent();
                        else if (this.active && !this.focused)
                            this.raiseFocusEvent();
                    }
                };
                tabItem.addEventListener("pointerup", clickHandler);
                return;
            }

            /**@type {HTMLDivElement|undefined} */
            var firstContainer = null;
            /**@type {HTMLDivElement|undefined} */
            var firstTabBar = null;
            /**@type {HTMLDivElement|undefined} */
            var highlightedTabItem = null;

            const rootNode = tabItem.getRootNode();
            const rootRenderer = PlanogramRenderer.get(this);
            down.preventDefault();
            down.stopPropagation();
            tabItem.setPointerCapture(down.pointerId);

            /**@type {HTMLElement} */
            const ghostTab = document.createElement("div");
            ghostTab.classList.add("tabGhost");
            ghostTab.style.left = `${down.clientX}px`;
            ghostTab.style.top = `${down.clientY}px`;
            ghostTab.innerText = this.cameraName;
            /**@type {HTMLElement} */

            const tabControlRoot = rootNode.querySelector(".tabControl.root");
            const cameraFrame = this.cameraFrame;
            // Check if there are other leaf frames (frames with no child frames) that have cameras
            const noOtherCameras = [...rootRenderer.querySelectorAll(`planogram-camera-frame`)].
                filter(x => x.childFrames.length === 0 && x !== cameraFrame).
                flatMap(x => x.associatedCameras).length === 0;
            const siblingLength = !cameraFrame || (cameraFrame?.associatedCameras.length === 1 && noOtherCameras);

            const mousemove = move => {
                if (siblingLength || Math.abs(down.clientX - move.clientX) < dragThreshold && Math.abs(down.clientY - move.clientY) < dragThreshold)
                    return;
                if (!tabControlRoot.contains(ghostTab))
                    tabControlRoot.appendChild(ghostTab);
                rootNode.querySelectorAll(".tabContainer").forEach(x => x.classList.remove(topHighlight, bottomHighlight, leftHighlight, rightHighlight));
                const offset = tabControlRoot.getBoundingClientRect();
                tabItem.style.cursor = "grabbing";
                ghostTab.style.cursor = "grabbing";
                ghostTab.style.left = `${move.clientX - offset.x}px`;
                ghostTab.style.top = `${move.clientY - offset.y}px`;

                [...rootNode.querySelectorAll(".tabBar")].flat().flatMap(x => [...x.children]).forEach(x => x.classList.remove("dragLeft", "dragRight"));

                firstTabBar = [...rootNode.querySelectorAll(".tabBar")].find(x => {
                    const boundingBox = x.getBoundingClientRect();
                    return move.clientX > boundingBox.left && move.clientX < boundingBox.right && move.clientY > boundingBox.top && move.clientY < boundingBox.bottom
                });

                firstContainer = [...rootNode.querySelectorAll(".tabContainer")].
                    filter(x => ![...x.querySelectorAll('*')].some(y => y.classList.contains("tabContainer"))).find(x => {
                        const boundingBox = x.getBoundingClientRect();
                        return move.clientX > boundingBox.left && move.clientX < boundingBox.right && move.clientY > boundingBox.top && move.clientY < boundingBox.bottom
                    });

                // if this tabitem is the only tab item in the tabcontrol then we can't split it's own container
                if (firstContainer) {

                    const frame = this.getCameraFrameFromUID(firstContainer.getAttribute(PlanogramCameraFrame.UnobservedAttributes.AttachedFrame));
                    if (frame && frame.canSplit(this.#tabItem)) {
                        const isSibling = frame.isSiblingFrame(this.#tabItem);
                        const parentDirection = frame.parentElement.childFrameDirection;
                        const rect = firstContainer.getBoundingClientRect();
                        const verticalMid = rect.top + rect.height / 2;
                        const horizontalMid = rect.left + rect.width / 2;
                        const dx = move.clientX - horizontalMid;
                        const dy = move.clientY - verticalMid;
                        firstContainer.classList.remove(topHighlight, bottomHighlight, leftHighlight, rightHighlight);
                        if (isSibling) {
                            if (parentDirection === PlanogramCameraFrame.frameType.Horizontal)
                                firstContainer.classList.add(move.clientY > verticalMid ? bottomHighlight : topHighlight);
                            else if (parentDirection === PlanogramCameraFrame.frameType.Vertical)
                                firstContainer.classList.add(move.clientX > horizontalMid ? rightHighlight : leftHighlight);
                        } else {
                            if (Math.abs(dx) > Math.abs(dy)) {
                                if (!isSibling || isSibling && parentDirection === PlanogramCameraFrame.frameType.Vertical)
                                    firstContainer.classList.add(dx > 0 ? rightHighlight : leftHighlight);
                            } else {
                                if (!isSibling || isSibling && parentDirection === PlanogramCameraFrame.frameType.Horizontal)
                                    firstContainer.classList.add(dy > 0 ? bottomHighlight : topHighlight);
                            }
                        }

                    }
                } else if (firstTabBar) {
                    const childTabItems = [...firstTabBar.children].filter(x => x.classList.contains("tabItem") && x !== tabItem);
                    highlightedTabItem = childTabItems.find((x, i) => {
                        const itemRect = x.getBoundingClientRect();
                        const isRight = move.clientX >= itemRect.x + itemRect.width / 2 && (move.clientX < itemRect.right || childTabItems.length === i + 1);
                        const isLeft = move.clientX < itemRect.x + itemRect.width / 2 && move.clientX > itemRect.left;
                        const value = isRight && x.nextElementSibling !== tabItem ? 1 : isLeft && x.previousElementSibling !== tabItem ? -1 : 0;
                        if (value !== 0) x.classList.add(value === 1 ? "dragRight" : value === -1 ? "dragLeft" : "");
                        return value !== 0;
                    });
                }

            };
            const mouseup = up => {
                tabItem.removeEventListener("pointermove", mousemove);
                tabItem.removeEventListener("pointerup", mouseup);
                ghostTab.remove();
                up.stopPropagation();

                if (Math.abs(down.clientX - up.clientX) < dragThreshold && Math.abs(down.clientY - up.clientY) < dragThreshold) {
                    if (!this.active)
                        this.raiseClickEvent();
                    else if (this.active && !this.focused)
                        this.raiseFocusEvent();
                } else {
                    if (firstContainer) {
                        const frame = this.getCameraFrameFromUID(firstContainer.getAttribute(PlanogramCameraFrame.UnobservedAttributes.AttachedFrame));
                        const anchor = firstContainer.classList.contains(topHighlight) ? 0 :
                            firstContainer.classList.contains(bottomHighlight) ? 1 :
                                firstContainer.classList.contains(leftHighlight) ? 2 :
                                    firstContainer.classList.contains(rightHighlight) ? 3 : null;
                        frame.raiseDropEvent(anchor, this.id);

                    } else if (highlightedTabItem) {
                        const anchor = highlightedTabItem.classList.contains("dragLeft") ? 2 :
                            highlightedTabItem.classList.contains("dragRight") ? 3 : null;
                        const camera = this.getCameraFromUID(highlightedTabItem.id);
                        this.raiseDropEvent(anchor, camera.id);
                    }

                }

                [...rootNode.querySelectorAll(".tabBar")].flatMap(x => [...x.children]).forEach(x => x.classList.remove("dragLeft", "dragRight"));
                [...rootNode.querySelectorAll(".tabContainer")].forEach(x => x.classList.remove(topHighlight, bottomHighlight, leftHighlight, rightHighlight));
                tabItem.releasePointerCapture(down.pointerId);
                tabItem.style.cursor = "pointer";

            };
            tabItem.addEventListener("pointermove", mousemove);
            tabItem.addEventListener("pointerup", mouseup);
        });

        tabItem.addEventListener("contextmenu", e => {
            e.preventDefault();
            e.stopPropagation();
            this.raiseContextMenuEvent(e.clientX, e.clientY);
        });
        this.#tabItem = tabItem;
        this.setActiveAndFocused();
        this.#setTabIcon();
        this.#setTabItemText();
        this.setTabDraggable();
        this.#setUpCloseButton()

    }

    #setTabItemText() {
        if (!this.#text) {
            this.#text = document.createElement("span");
            this.#tabItem.appendChild(this.#text);
        }
        this.#text.innerText = this.cameraName;
    }

    #setTabIcon() {
        if (this.icon) {
            if (!this.#icon) {
                this.#icon = document.createElement("span");
                this.#icon.classList.add("tabIcon");
                this.#icon.innerText = this.icon;
                if (this.#text) {
                    this.#text.insertAdjacentElement("beforebegin", this.#icon);
                } else {
                    this.#tabItem.appendChild(this.#icon);
                }
            } else {
                this.#icon.innerText = this.icon;
            }
        } else {
            if (this.#icon) {
                this.#icon.remove();
                this.#icon = null;
            }
        }

    }

    #setUpCloseButton() {
        if (!this.disableClose && !this.#closebutton) {
            const button = document.createElement("button");
            button.innerHTML = `<svg height="16px" width="16px" xmlns="http://www.w3.org/2000/svg">
                <line x1="4" y1="4" x2="12" y2="12"></line>
                <line x1="12" y1="4" x2="4" y2="12"></line>
            </svg>`;
            button.addEventListener("click", e => {
                e.preventDefault();
                e.stopPropagation();

                this.raiseCancelEvent();
            });
            this.#closebutton = button;
            this.#tabItem.appendChild(button);
        }
        if (!this.disableClose && !this.#tabItem.contains(this.#closebutton)) this.#tabItem.appendChild(this.#closebutton);
        else if (this.disableClose && this.#tabItem.contains(this.#closebutton)) this.#closebutton.remove();

    }

    /**
     * 
     * @param {HTMLDivElement} tabBar 
     */
    addToTabBar(tabBar) {
        if (!tabBar) return;
        if (!this.#tabItem && !this.collapsed) {
            this.#setUpCamera();
            this.#setUpTabItem();
        } else if (this.#tabItem && this.collapsed && tabBar.contains(this.#tabItem)) {
            tabBar.removeChild(this.#tabItem);
        }
        if (this.#tabItem && !this.collapsed) {
            if (!tabBar.contains(this.#tabItem))
                tabBar.appendChild(this.#tabItem);
        }
        this.setActiveAndFocused();
    }

    setActiveAndFocused() {
        if (this.#tabItem) {
            if (this.active && !this.#tabItem.classList.contains("active")) {
                this.#tabItem.classList.add("active");
                if (!this.#firstRender && this.renderer) {
                    this.setBehaviour(this.renderer.tabActiveBehaviour);
                }
            }
            else if (!this.active && this.#tabItem.classList.contains("active"))
                this.#tabItem.classList.remove("active");
        }

        if (this.isConnected && this.#tabItem && this.#tabItem.parentElement) {
            this.cameraFrame?.updateFocus();
            this.cameraFrame?.createToolBar();
        }

        if (this.active && this.focused)
            this.#camera.layers.enable(PlanogramRendererResources.DECORATOR_LAYER);
        else if (!this.focused)
            this.#camera.layers.disable(PlanogramRendererResources.DECORATOR_LAYER);
    }

    setTabDraggable() {
        if (!this.renderer) return;
        this.#tabItem.draggable = !this.lockTab && !this.renderer.disableTabDrag;
    }

    setIndex() {
        /**@type {PlanogramCameraFrame} */
        const cameraFrame = this.cameraFrame;
        cameraFrame?.updateTabItems();
    }

    /**
     * 
     * @param {string} uid 
     * @returns {PlanogramCameraFrame|undefined}
     */
    getCameraFrameFromUID(uid) { return [...this.getRootNode().querySelectorAll("planogram-camera-frame")].find(x => x.uid === uid); }
    /**
     * 
     * @param {string} uid 
     * @returns {PlanogramCamera|undefined}
     */
    getCameraFromUID(uid) { return [...this.getRootNode().querySelectorAll("planogram-camera")].find(x => x.uid === uid); }


    //#endregion

    //#region Camera Frame Methods
    #setCameraFrameChangeBehaviour(oldId, newId) {
        const renderer = PlanogramRenderer.get(this);
        /** @type {PlanogramCameraFrame} */
        // If newId is provided, try to find frame by UID
        var newFrame = null;
        if (newId) {
            const frames = [...renderer.querySelectorAll('planogram-camera-frame')];
            newFrame = frames.find(f => f.uid === newId);
        }
        /** @type {PlanogramCameraFrame} */
        var oldFrame = this.cameraFrame; // Use current parent before moving

        // Physically move this camera element to the new frame
        if (newFrame && this.parentElement !== newFrame) {
            newFrame.appendChild(this);
        }

        const assertSplit = newFrame && newFrame.associatedCameras.length === 1; // Now it's 1 because we just moved the camera
        if (assertSplit) {
            this.setBehaviour(renderer.splitCameraBehaviour);
            if (oldFrame && oldFrame.associatedCameras)
                oldFrame.associatedCameras.find(x => x.active)?.setBehaviour(renderer.splitCameraBehaviour);
        }
        oldFrame?.updateTabItems();
        newFrame?.updateTabItems();
    }

    #collapseTabItem() {
        /** @type {PlanogramCameraFrame} */
        const newFrame = this.cameraFrame;
        newFrame?.updateTabItems();
    }
    //#endregion

    //#region Event Methods
    /**
     * 
     * @param {PointerEvent} e 
     */
    #pointerDown = e => {
        if (!(e instanceof PointerEvent) || e.pointerType !== "mouse" || e.button !== 2 || !this.#movementControls.enabled || this.dockVector === null) return;
        const { x, y, z, w } = this.dockVector;
        if (e.offsetX < x || e.offsetX > x + z || e.offsetY < y || e.offsetY > y + w) return;
        this.pointerdownCoordinates = new THREE.Vector2(e.offsetX, e.offsetY);
    }

    /**
     * 
     * @param {PointerEvent} e 
     */
    #pointerUp = e => {
        const withinThreshold = PlanogramContainer.isWithinThreshold(this.pointerdownCoordinates, new THREE.Vector2(e.offsetX, e.offsetY));
        if (!(e instanceof PointerEvent) || !this.pointerdownCoordinates || withinThreshold) return;
        this.syncLockVerticalLayout();
        /**@type {THREE.Camera} */
        const camera = this.#movementControls.object;
        const cameraPosition = new THREE.Vector3().copy(camera.position);
        const lookAt = new THREE.Vector3().copy(this.#movementControls.target);
        this.pointerdownCoordinates = null;
        this.setAttribute(PlanogramCamera.Attributes.PositionZ, cameraPosition.z);
        this.setAttribute(PlanogramCamera.Attributes.PositionX, cameraPosition.x);
        this.setAttribute(PlanogramCamera.Attributes.PositionY, cameraPosition.y);
        this.setAttribute(PlanogramCamera.Attributes.LookAtX, lookAt.x);
        this.setAttribute(PlanogramCamera.Attributes.LookAtY, lookAt.y);
        this.setAttribute(PlanogramCamera.Attributes.LookAtZ, lookAt.z);
        this.#camera.updateProjectionMatrix();
        this.raiseMoveEvent(cameraPosition, lookAt);
        this.releasePointerCapture(e.pointerId);
    }
    //#endregion 

    //#region Event Raise Methods

    raiseMoveEvent(position, lookAt) {
        const data = { position: { x: position.x, y: position.y, z: position.z }, lookat: { x: lookAt.x, y: lookAt.y, z: lookAt.z } };
        const move = new CustomEvent(PlanogramCamera.customEvents.CameraMoved, { bubbles: true, cancelable: true, view: window, detail: data });
        if (this.dispatched(move)) {
            console.groupCollapsed("Camera Moved");
            console.log("Target: ", this);
            console.log("Event: ", move);
            console.log("Position: ", position);
            console.log("Look At: ", lookAt);
            console.groupEnd();
        }
    }

    raiseDropEvent(anchor, uid) {
        // const drop = new DragEvent("drop", { bubbles: true, cancelable: true, view: window, clientX: anchor,  dataTransfer: new DataTransfer() });
        // drop.dataTransfer.setData(uid, uid);
        const drop = new CustomEvent(PlanogramCamera.customEvents.TabMoved, { bubbles: true, cancelable: true, view: window, detail: { anchor, uid } });
        if (this.dispatched(drop)) {
            console.groupCollapsed("Tab Moved");
            console.log("Target: ", this);
            console.log("Event: ", drop);
            console.log("Direction: ", anchor);
            console.log("Anchor Id: ", uid);
            console.groupEnd();
        }
    }

    raiseFocusEvent() {
        const focusEvt = new FocusEvent("focus", { bubbles: true, cancelable: true, view: window });
        if (this.dispatched(focusEvt)) {
            console.groupCollapsed("Camera Focused");
            console.log("Target: ", this);
            console.log("Event: ", focusEvt);
            console.groupEnd();
        }
    }

    raiseBlurEvent() {
        const blurEvt = new FocusEvent("blur", { bubbles: true, cancelable: true, view: window });
        if (this.dispatched(blurEvt)) {
            console.groupCollapsed("Camera Blurred");
            console.log("Target: ", this);
            console.log("Event: ", blurEvt);
            console.groupEnd();
        }
    }

    raiseContextMenuEvent(x, y, type = 0) {
        const contextMenu = new MouseEvent("contextmenu", { bubbles: true, cancelable: true, view: window, clientX: x, clientY: y, screenX: type });
        if (this.dispatched(contextMenu)) {
            console.groupCollapsed("Tab Context Menu Invoked");
            console.log("Target: ", this);
            console.log("Event: ", contextMenu);
            console.log("Position: ", { x, y });
            console.groupEnd();
        }
    }

    raiseClickEvent() {
        const clickEvent = new MouseEvent("click", { bubbles: true, cancelable: true, view: window });
        if (this.dispatched(clickEvent)) {
            console.groupCollapsed("Tab Clicked");
            console.log("Target: ", this);
            console.log("Event: ", clickEvent);
            console.groupEnd();
        }
    }

    raiseCancelEvent() {
        const cancelEvent = new Event("cancel", { bubbles: true, cancelable: true, view: window });
        if (this.dispatched(cancelEvent)) {
            console.groupCollapsed("Tab Closed");
            console.log("Target: ", this);
            console.log("Event: ", cancelEvent);
            console.groupEnd();
        }
    }

    dispatched(event) {
        Object.defineProperty(event, 'target', { ...Object.getOwnPropertyDescriptor(event, 'target'), get: () => { return this; } });
        const renderer = PlanogramRenderer.get(this)?.showMessages ?? false;
        const dropped = this.dispatchEvent(event);
        return dropped && renderer;
    }

    //#endregion

    //#region WebComponent Lifecycle CallBacks
    connectedCallback() {
        this.#setUpCamera();
        this.#setCameraPosition();
        this.#setUpTabItem();
        this.#firstRender = false;
        this.renderer = PlanogramRenderer.get(this);
        const associatedFrame = this.cameraFrame;

        // Camera should already be a child of its frame in the DOM
        if (associatedFrame && associatedFrame instanceof PlanogramCameraFrame) {
            associatedFrame.addCameras(this);
        }

        this.setAttribute("data-ignore-context", "ignore");
        if (this.active || this.focused) {
            this.setActiveAndFocused();
            // Apply tab behavior during initial connection if camera is active
            if (this.active && this.renderer) {
                this.setBehaviour(this.renderer.tabActiveBehaviour);
            }
        }
        // Grid setup moved to onContainerReady() to handle initialization order
        if (this.container) {
            this.onContainerReady();
        }
        this.syncLockVerticalLayout(true);
        PlanogramRenderer.applyToRelay(this, PlanogramCamera.customEvents);
    }

    disconnectedCallback() {
        if (this.#containerObserver) {
            this.#containerObserver.disconnect();
            this.#containerObserver = null;
        }
        this.#disposeMovementControls();
        this.#removeListeners();
        this.#disposeGrid();
        this.#disposeLockVerticalScrollbar();
        this.#disposeMiniMap();
        if (this.#tabItem) {
            this.#tabItem.remove();
            this.#tabItem = null;
        }
        this.renderer = null;
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (this.#firstRender || oldValue === newValue) return;

        if (this.#writingLockVerticalState && [
            PlanogramCamera.Attributes.PositionX,
            PlanogramCamera.Attributes.PositionY,
            PlanogramCamera.Attributes.PositionZ,
            PlanogramCamera.Attributes.LookAtX,
            PlanogramCamera.Attributes.LookAtY,
            PlanogramCamera.Attributes.LookAtZ
        ].includes(name)) {
            return;
        }

        if (name === PlanogramCamera.Attributes.Active) {
            this.#movementControls.enabled = this.active;
            this.#movementControls.update();
            this.syncLockVerticalLayout();
            this.syncMiniMapLayout();
            // Apply tab behavior when camera becomes active
            if (this.active && this.renderer) {
                this.setBehaviour(this.renderer.tabActiveBehaviour);
            }
        }
        if (name === PlanogramCamera.Attributes.DockRow && this.active)
            this.#movementControls.update();
        if ([PlanogramCamera.Attributes.PositionX, PlanogramCamera.Attributes.PositionY, PlanogramCamera.Attributes.PositionZ].includes(name)) {
            this.#setCameraPosition();
            this.#markLockVerticalDirty();
        }
        if (name === PlanogramCamera.Attributes.Type) {
            this.#markLockVerticalDirty(true);
            this.#miniMapDirty = true;
            this.#setUpCamera();
        }
        if ([PlanogramCamera.Attributes.Control, PlanogramCamera.Attributes.ZoomDisabled, PlanogramCamera.Attributes.LockVertical, PlanogramCamera.Attributes.MiniMap].includes(name)) {
            this.#markLockVerticalDirty(true);
            this.#miniMapDirty = true;
            this.#updateMovementControls();
            this.#setUpGrid();
            this.syncLockVerticalLayout(true);
            this.syncMiniMapLayout();
        }
        if ([PlanogramCamera.Attributes.LookAtX, PlanogramCamera.Attributes.LookAtY, PlanogramCamera.Attributes.LookAtZ].includes(name)) {
            this.#movementControls.target.copy(this.cameraLookAt);
            this.#movementControls.update();
            this.#markLockVerticalDirty();
        }

        if (name === PlanogramCamera.Attributes.Collapsed)
            this.#collapseTabItem();
        if (name === PlanogramCamera.Attributes.CameraName)
            this.#setTabItemText();
        if (name === PlanogramCamera.Attributes.Icon)
            this.#setTabIcon();
        if (name === PlanogramCamera.Attributes.Focused || name === PlanogramCamera.Attributes.Active)
            this.setActiveAndFocused();
        if (name === PlanogramCamera.Attributes.DisableClose)
            this.#setUpCloseButton();
        // Removed: CameraFrameId case - no longer used (use DOM hierarchy)
        if (name === PlanogramCamera.Attributes.CameraFrameIndex)
            this.setIndex();
        if (name === PlanogramCamera.Attributes.ShowGrid)
            this.#setUpGrid();


    }
    //#endregion

    //#region Static properties
    static cameraType = new Map([
        ["Orthographic", "Orthographic"],
        ["Perspective", "Perspective"]
    ]);

    static cameraControl = new Map([
        ["Orbit", "Orbit"],
        ["Dolly", "Dolly"]
    ]);

    static anchor = {
        top: 0,
        bottom: 1,
        left: 2,
        right: 3
    };

    static dropType = {
        Frame: 0,
        Tab: 1
    };

    static customEvents = {
        CameraMoved: "cameramoved",
        TabMoved: "tabmoved"
    };
    //#endregion

}